<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BarChart from 'shared/components/charts/BarChart.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useReportFormatter } from 'dashboard/composables/useReportFormatter';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';
import AnalyticsPanel from '../analytics/AnalyticsPanel.vue';
import MetricValue from '../analytics/MetricValue.vue';
import { dayLabel } from '../analytics/analyticsHelpers';
import { useAnalyticsChart } from '../analytics/useAnalyticsChart';
import { useAnalyticsFormat } from '../analytics/useAnalyticsFormat';
import MapaSemanaHora from '../MapaSemanaHora.vue';
import {
  COUNTS,
  FIELDS,
  TIMES,
  bucketLabel,
  mapaCsv,
  numerosCsv,
} from './numerosCsv';
import { QUEM_CORES } from './quemCores';

// A aba Números (REPORTS-04): os totais do período, cada número separado e sem
// somar tipos diferentes, por dia, semana ou mês, e os mapas de calor de
// tráfego e de resolução. As contagens do total são a soma dos baldes; a
// mediana do total vem do servidor, e não dos baldes (D16). O CSV sai do mesmo
// JSON (D2). Nada de custo, token ou modelo (R2 da VISAO-01).
const props = defineProps({
  // A resposta de GET atendimento/numeros; null enquanto não chegou.
  report: { type: Object, default: null },
  failed: { type: Boolean, default: false },
  // A resposta de GET atendimento/mapa, pedida à parte: o agrupamento não a muda.
  mapa: { type: Object, default: null },
  mapaFailed: { type: Boolean, default: false },
});

// dia | semana | mes. A tela escreve os baldes pelo agrupamento da resposta
// (`report.grain`), e não pelo pedido: até a nova chegar, vale a que está nela.
const grain = defineModel('grain', { type: String, default: 'dia' });

const { t } = useI18n();
const { number, duration } = useAnalyticsFormat();

// O período dos números na tela, o da própria resposta: a página só recarrega
// a aba aberta, e o de outra seção lida antes pode ser outro.
const rangeLabel = computed(() => {
  const period = props.report?.period;
  return period
    ? t('ATENDIMENTO_REPORTS.FILTERS.RANGE', {
        since: dayLabel(period.since),
        until: dayLabel(period.until),
      })
    : '';
});
const { weekdayOf } = useReportFormatter();
const { palette, bar, chartOptions } = useAnalyticsChart();

const GRAINS = {
  dia: 'ATENDIMENTO_REPORTS.NUMBERS.GRAINS.DIA',
  semana: 'ATENDIMENTO_REPORTS.NUMBERS.GRAINS.SEMANA',
  mes: 'ATENDIMENTO_REPORTS.NUMBERS.GRAINS.MES',
};
const CHART_TITLES = {
  dia: 'ATENDIMENTO_REPORTS.NUMBERS.CHART.DIA',
  semana: 'ATENDIMENTO_REPORTS.NUMBERS.CHART.SEMANA',
  mes: 'ATENDIMENTO_REPORTS.NUMBERS.CHART.MES',
};

// Aviso novo que a tela não conhece aparece cru: melhor que sumir.
const NOTICES = {
  resolucoes_antes_de_30_09:
    'ATENDIMENTO_REPORTS.NUMBERS.NOTICES.RESOLUCOES_ANTES_DE_30_09',
  disparo_fora_do_os: 'ATENDIMENTO_REPORTS.NUMBERS.NOTICES.DISPARO_FORA_DO_OS',
  robo_externo: 'ATENDIMENTO_REPORTS.NUMBERS.NOTICES.ROBO_EXTERNO',
  tempos_sem_robo_externo:
    'ATENDIMENTO_REPORTS.NUMBERS.NOTICES.TEMPOS_SEM_ROBO_EXTERNO',
};
const notices = computed(() =>
  (props.report?.notices || []).map(key => ({
    key,
    text: NOTICES[key] ? t(NOTICES[key]) : key,
  }))
);

const totals = computed(() => props.report?.totals);
const rows = computed(() => props.report?.series || []);

// O robô externo (P1-b) só aparece na conta que tem um: nas outras, seria um
// zero sem nada a dizer.
const hasExternalBot = computed(() => totals.value?.external_bot_messages > 0);

// As do suporte Powerbizz já estão nas de pessoas: na tela, só como dica.
const tableCounts = computed(() =>
  COUNTS.filter(
    key =>
      key !== 'human_messages_support' &&
      (key !== 'external_bot_messages' || hasExternalBot.value)
  )
);
const TIME_KEYS = Object.keys(TIMES);

// A IA da Números é a de A IA (D39): a conversa de antes do agente e a caixa
// de robô externo ficam fora dela, e não das recebidas nem das de pessoas. A
// dica diz por que ela bate com a outra aba, e não com o volume ao lado.
const aiHint = computed(() => t('ATENDIMENTO_REPORTS.NUMBERS.HINTS.AI'));
const COUNT_HINTS = {
  received: () => t('ATENDIMENTO_REPORTS.NUMBERS.HINTS.RECEIVED'),
  ai_messages: () => aiHint.value,
  human_messages: all =>
    all.human_messages_support
      ? t('ATENDIMENTO_REPORTS.NUMBERS.HINTS.SUPPORT', {
          count: number(all.human_messages_support),
        })
      : '',
  external_bot_messages: () =>
    t('ATENDIMENTO_REPORTS.NUMBERS.HINTS.EXTERNAL_BOT'),
};
const counts = computed(() =>
  tableCounts.value.map(key => ({
    key,
    label: t(FIELDS[key]),
    value: totals.value[key],
    hint: COUNT_HINTS[key]?.(totals.value) || '',
  }))
);

// Time e atendente não se aplicam aos disparos (D9): o servidor diz quais
// ficaram de fora.
const dispatchesIgnoreFilters = computed(
  () => props.report?.ignored_filters?.dispatches?.length > 0
);

// "mediana · média 3 min · 42 amostras · 5 sem resposta". Sem amostra, a
// mediana e a média são null: o cartão diz "sem dado", e não 0.
const timeHint = stat =>
  [
    typeof stat.avg === 'number'
      ? t('ATENDIMENTO_REPORTS.NUMBERS.HINTS.TIME', {
          average: duration(stat.avg),
        })
      : '',
    t(
      'ATENDIMENTO_REPORTS.NUMBERS.HINTS.SAMPLES',
      { count: number(stat.n) },
      stat.n
    ),
    'unanswered' in stat
      ? t(
          'ATENDIMENTO_REPORTS.NUMBERS.HINTS.UNANSWERED',
          { count: number(stat.unanswered) },
          stat.unanswered
        )
      : '',
  ]
    .filter(Boolean)
    .join(' · ');
const times = computed(() =>
  TIME_KEYS.map(key => {
    const stat = totals.value[key];
    return {
      key,
      label: t(FIELDS[key]),
      value: stat.p50,
      reason: stat.n ? '' : t('ATENDIMENTO_REPORTS.NUMBERS.NO_SAMPLES'),
      hint: timeHint(stat),
    };
  })
);

const labelOf = row => bucketLabel(t, props.report.grain, row);

// As mensagens por balde, cada quem na sua barra: o disparo nunca soma na IA,
// nem a IA no disparo.
const chartSeries = computed(() => [
  {
    key: 'received',
    label: t('ATENDIMENTO_REPORTS.NUMBERS.CHART.RECEIVED'),
    color: 'espera',
  },
  {
    key: 'ai_messages',
    label: t('ATENDIMENTO_REPORTS.NUMBERS.CHART.AI'),
    color: 'ia',
  },
  {
    key: 'human_messages',
    label: t('ATENDIMENTO_REPORTS.NUMBERS.CHART.PEOPLE'),
    color: 'pessoa',
  },
  {
    key: 'dispatches_sent',
    label: t('ATENDIMENTO_REPORTS.NUMBERS.CHART.DISPATCHES'),
    color: 'disparo',
  },
  ...(hasExternalBot.value
    ? [
        {
          key: 'external_bot_messages',
          label: t('ATENDIMENTO_REPORTS.NUMBERS.CHART.EXTERNAL_BOT'),
          color: 'muted',
        },
      ]
    : []),
]);
const legendClass = color => QUEM_CORES[color]?.bg || 'bg-n-slate-10';
const hasMessages = computed(() =>
  chartSeries.value.some(item => rows.value.some(row => row[item.key] > 0))
);
const collection = computed(() => ({
  labels: rows.value.map(labelOf),
  datasets: chartSeries.value.map(item =>
    bar(
      item.label,
      rows.value.map(row => row[item.key]),
      palette.value[item.color]
    )
  ),
}));
const options = computed(() => chartOptions(number));

const hourLabel = hour => t('ATENDIMENTO_REPORTS.NUMBERS.MAPS.HOUR', { hour });

// Os dois mapas: o tráfego (as conversas novas, pela hora em que chegaram) e a
// resolução (as resoluções, pela hora em que aconteceram).
const MAPS = [
  {
    key: 'new_conversations',
    testId: 'atendimento-mapa-novas',
    title: 'ATENDIMENTO_REPORTS.NUMBERS.MAPS.TRAFFIC',
    cell: 'ATENDIMENTO_REPORTS.NUMBERS.MAPS.TRAFFIC_CELL',
    empty: 'ATENDIMENTO_REPORTS.NUMBERS.MAPS.EMPTY_TRAFFIC',
  },
  {
    key: 'resolved',
    testId: 'atendimento-mapa-resolvidas',
    title: 'ATENDIMENTO_REPORTS.NUMBERS.MAPS.RESOLUTION',
    cell: 'ATENDIMENTO_REPORTS.NUMBERS.MAPS.RESOLUTION_CELL',
    empty: 'ATENDIMENTO_REPORTS.NUMBERS.MAPS.EMPTY_RESOLUTION',
  },
].map(map => ({
  ...map,
  valueOf: cell => cell[map.key],
  cellText: (cell, day) =>
    t(
      map.cell,
      {
        when: t('ATENDIMENTO_REPORTS.NUMBERS.MAPS.WHEN', {
          day,
          hour: hourLabel(cell.hour),
        }),
        count: number(cell[map.key]),
      },
      cell[map.key]
    ),
}));
const mapHasData = key => props.mapa.cells.some(cell => cell[key] > 0);

const downloadNumeros = () => {
  const { since, until } = props.report.period;
  downloadCsvFile(
    `numeros_${since}_${until}_${props.report.grain}.csv`,
    numerosCsv(props.report, t)
  );
};
const downloadMapa = () => {
  const { since, until } = props.mapa.period;
  downloadCsvFile(
    `mapas_${since}_${until}.csv`,
    mapaCsv(props.mapa, t, weekdayOf)
  );
};
</script>

<template>
  <div class="flex flex-col gap-4 min-w-0">
    <AnalyticsPanel
      data-test-id="atendimento-numeros"
      :title="t('ATENDIMENTO_REPORTS.NUMBERS.TITLE')"
      :description="
        rangeLabel
          ? t('ATENDIMENTO_REPORTS.NUMBERS.DESCRIPTION', { range: rangeLabel })
          : ''
      "
    >
      <template #aside>
        <div
          role="group"
          :aria-label="t('ATENDIMENTO_REPORTS.NUMBERS.GRAIN')"
          class="flex flex-wrap p-0.5 max-w-full rounded-lg border border-n-weak bg-n-alpha-1"
        >
          <button
            v-for="(label, key) in GRAINS"
            :key="key"
            type="button"
            :data-test-id="`atendimento-numeros-agrupar-${key}`"
            :aria-pressed="grain === key"
            class="px-2.5 py-1 text-xs font-medium rounded-md transition-colors"
            :class="
              grain === key
                ? 'bg-n-solid-3 text-n-slate-12 shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="grain = key"
          >
            {{ t(label) }}
          </button>
        </div>
      </template>

      <p v-if="failed" class="mb-0 text-sm text-n-ruby-11">
        {{ t('ATENDIMENTO_REPORTS.FAILED') }}
      </p>

      <template v-else-if="report">
        <p
          v-for="notice in notices"
          :key="notice.key"
          :data-test-id="`atendimento-numeros-aviso-${notice.key}`"
          class="px-3 py-2 mb-0 text-xs rounded-lg bg-n-amber-3 text-n-amber-11"
        >
          {{ notice.text }}
        </p>

        <dl class="grid grid-cols-2 gap-3 m-0 min-w-0 lg:grid-cols-4">
          <div
            v-for="item in counts"
            :key="item.key"
            :data-test-id="`atendimento-numeros-${item.key}`"
            class="flex flex-col gap-1 p-3 min-w-0 rounded-lg border border-n-weak"
          >
            <dt class="text-sm text-n-slate-11">{{ item.label }}</dt>
            <dd
              :data-test-id="`atendimento-numeros-${item.key}-value`"
              class="m-0 text-2xl font-medium tabular-nums text-n-slate-12"
            >
              {{ number(item.value) }}
            </dd>
            <span v-if="item.hint" class="text-xs text-n-slate-11">
              {{ item.hint }}
            </span>
          </div>
        </dl>
        <p
          v-if="dispatchesIgnoreFilters"
          data-test-id="atendimento-numeros-disparos-filtros"
          class="mb-0 text-xs text-n-slate-11"
        >
          {{ t('ATENDIMENTO_REPORTS.NUMBERS.HINTS.DISPATCHES') }}
        </p>

        <dl class="grid grid-cols-2 gap-3 m-0 min-w-0 lg:grid-cols-4">
          <div
            v-for="item in times"
            :key="item.key"
            :data-test-id="`atendimento-numeros-${item.key}`"
            class="flex flex-col gap-1 p-3 min-w-0 rounded-lg border border-n-weak"
          >
            <dt class="text-sm text-n-slate-11">{{ item.label }}</dt>
            <dd class="m-0">
              <MetricValue
                :data-test-id="`atendimento-numeros-${item.key}-value`"
                :value="item.value"
                :format="duration"
                :reason="item.reason"
                class="text-2xl font-medium tabular-nums break-words text-n-slate-12"
              />
            </dd>
            <span class="text-xs text-n-slate-11">{{ item.hint }}</span>
          </div>
        </dl>

        <section class="flex flex-col gap-2 min-w-0">
          <header
            class="flex flex-wrap gap-x-4 gap-y-1 justify-between items-center"
          >
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ t(CHART_TITLES[report.grain]) }}
            </h3>
            <div
              class="flex flex-wrap gap-3 items-center text-xs text-n-slate-11"
            >
              <span
                v-for="item in chartSeries"
                :key="item.key"
                class="flex gap-1.5 items-center"
              >
                <span
                  class="rounded-sm size-2.5"
                  :class="legendClass(item.color)"
                />
                {{ item.label }}
              </span>
            </div>
          </header>
          <div class="relative h-56">
            <BarChart
              v-if="hasMessages"
              :collection="collection"
              :chart-options="options"
            />
            <p
              v-else
              data-test-id="atendimento-numeros-grafico-vazio"
              class="flex justify-center items-center mb-0 h-full text-sm text-n-slate-11"
            >
              {{ t('ATENDIMENTO_REPORTS.NUMBERS.CHART.EMPTY') }}
            </p>
          </div>
        </section>

        <section class="flex flex-col gap-2 min-w-0">
          <header
            class="flex flex-wrap gap-x-4 gap-y-1 justify-between items-center"
          >
            <p class="mb-0 text-xs text-n-slate-11">
              {{ t('ATENDIMENTO_REPORTS.NUMBERS.TABLE.HINT') }}
            </p>
            <Button
              data-test-id="atendimento-numeros-csv"
              :label="t('ATENDIMENTO_REPORTS.NUMBERS.CSV.DOWNLOAD')"
              icon="i-ph-download-simple"
              faded
              slate
              xs
              type="button"
              @click="downloadNumeros"
            />
          </header>
          <div class="overflow-x-auto max-w-full">
            <table
              data-test-id="atendimento-numeros-tabela"
              class="w-full text-sm whitespace-nowrap"
            >
              <thead>
                <tr class="text-xs text-n-slate-11">
                  <th class="py-2 pe-4 font-medium text-start">
                    {{ t('ATENDIMENTO_REPORTS.NUMBERS.TABLE.PERIOD') }}
                  </th>
                  <th
                    v-for="key in [...tableCounts, ...TIME_KEYS]"
                    :key="key"
                    :title="key === 'ai_messages' ? aiHint : undefined"
                    class="py-2 ps-4 font-medium text-end"
                  >
                    {{ t(FIELDS[key]) }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <tr
                  v-for="row in rows"
                  :key="row.bucket"
                  :data-test-id="`atendimento-numeros-balde-${row.bucket}`"
                >
                  <td class="py-2 pe-4 text-n-slate-12">{{ labelOf(row) }}</td>
                  <td
                    v-for="key in tableCounts"
                    :key="key"
                    class="py-2 ps-4 tabular-nums text-end text-n-slate-12"
                  >
                    {{ number(row[key]) }}
                  </td>
                  <td
                    v-for="key in TIME_KEYS"
                    :key="key"
                    class="py-2 ps-4 tabular-nums text-end text-n-slate-12"
                  >
                    <MetricValue :value="row[key].p50" :format="duration" />
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr
                  data-test-id="atendimento-numeros-total"
                  class="font-medium border-t border-n-strong"
                >
                  <td class="py-2 pe-4 text-n-slate-12">
                    {{ t('ATENDIMENTO_REPORTS.NUMBERS.TOTAL') }}
                  </td>
                  <td
                    v-for="key in tableCounts"
                    :key="key"
                    class="py-2 ps-4 tabular-nums text-end text-n-slate-12"
                  >
                    {{ number(totals[key]) }}
                  </td>
                  <td
                    v-for="key in TIME_KEYS"
                    :key="key"
                    class="py-2 ps-4 tabular-nums text-end text-n-slate-12"
                  >
                    <MetricValue :value="totals[key].p50" :format="duration" />
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      </template>
    </AnalyticsPanel>

    <AnalyticsPanel
      data-test-id="atendimento-mapas"
      :title="t('ATENDIMENTO_REPORTS.NUMBERS.MAPS.TITLE')"
      :description="
        mapa?.timezone
          ? t('ATENDIMENTO_REPORTS.NUMBERS.MAPS.DESCRIPTION', {
              timezone: mapa.timezone,
            })
          : ''
      "
    >
      <template v-if="mapa" #aside>
        <Button
          data-test-id="atendimento-mapas-csv"
          :label="t('ATENDIMENTO_REPORTS.NUMBERS.CSV.DOWNLOAD')"
          icon="i-ph-download-simple"
          faded
          slate
          xs
          type="button"
          @click="downloadMapa"
        />
      </template>

      <p v-if="mapaFailed" class="mb-0 text-sm text-n-ruby-11">
        {{ t('ATENDIMENTO_REPORTS.FAILED') }}
      </p>
      <template v-else-if="mapa">
        <section
          v-for="map in MAPS"
          :key="map.key"
          :data-test-id="map.testId"
          class="flex flex-col gap-2 min-w-0"
        >
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t(map.title) }}
          </h3>
          <MapaSemanaHora
            v-if="mapHasData(map.key)"
            :test-id="map.testId"
            :cells="mapa.cells"
            :value-of="map.valueOf"
            :cell-text="map.cellText"
          />
          <p v-else class="mb-0 text-sm text-n-slate-11">
            {{ t(map.empty) }}
          </p>
        </section>
      </template>
    </AnalyticsPanel>
  </div>
</template>
